// Строка матча (Figma Match Item 795:39593, HQ-1899): слева статус шириной 54 (live / время / итог),
// команды с логотипом 18 и счётом (серый, победитель не выделяется), звезда; под ними — фраза состояния капсулой
// во всю ширину (StatusBadge 795:39563: зелёная у живого, серая у прочих; tone=win — зелёная у итога). Серой строки «раунд · стадион» в макете нет — матч-центр и поиск её выключают (meta={false}). Модель — matchRowModel.
import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import type { MatchRow } from '../api/types';
import { formatDate, t } from '../i18n/i18n';
import { FavoriteStar } from './FavoriteStar';
import { rowKeyHandler } from './rowKey';
import { StatusBadge, badgeTone } from './StatusBadge';
import { buildRowModel, formatStartParts, formatStartText } from './matchRowModel';
import { TeamLogo } from './TeamLogo';
import styles from './MatchRowView.module.css';
import { anchorAttr } from './spotlight/anchors';

// Ступени подгонки подписи статуса (HQ-1166): в колонке 54 (подпись до 62, 2 строки по словам) 13 → 12 → 11 — пока влезает,
// колонка и имя команды прежние; не влезло и в 11 (шрифт ×2, перевод) — колонка шириной в самое длинное слово (≤ 96),
// затем до 96 (как у плашки badge, HQ-1113); не влезло и так (слово шире 96, неизвестный длинный статус) — перенос и
// по буквам, без ограничения строк. Многоточия нет ни на одной ступени. Колонка расширяется последней: при ×2 на 360 px
// колонка 96 оставляла имени 130 px — «Bangladesh» рвался по буквам.
const SIDE_STEPS = [styles.sideText, `${styles.sideText} ${styles.sideStep1}`, `${styles.sideText} ${styles.sideStep2}`,
  `${styles.sideText} ${styles.sideStep2} ${styles.sideTextWide} ${styles.sideTextTight}`,
  `${styles.sideText} ${styles.sideStep2} ${styles.sideTextWide}`, `${styles.sideText} ${styles.sideStep2} ${styles.sideTextWide} ${styles.sideBreak}`];
const FIRST_WIDE = 3;

/** Колонка статуса с подписью («Abandoned», «Innings break», перевод): слово внутри не рвём — не влезло слово
 *  или больше 2 строк — следующая ступень (кегль, затем ширина колонки); последняя переносит по буквам.
 *  key у вызова = текст: новая подпись — с первой ступени. */
function SideText({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [step, setStep] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && step < SIDE_STEPS.length - 1
      && (el.scrollWidth > el.clientWidth + 0.5 || el.scrollHeight > el.clientHeight + 0.5)) setStep(step + 1);
  }, [step]);
  return (
    <div className={`${styles.side} ${step >= FIRST_WIDE ? styles.sideWide : ''}`}>
      <span ref={ref} className={SIDE_STEPS[step]}>{text}</span>
    </div>
  );
}

/** Тон капсулы итога сыгранного матча: в списке матчей чемпионата — зелёная (Figma 1025:143087), в матч-центре,
 *  клубе и избранном — серая (772:33037, 1025:143857, 1073:19579). Свойство tone строки сильнее провайдера (HQ-1899);
 *  провайдер оставлен экранам, которые уже его ставят. */
export type ResultTone = 'neutral' | 'win';
export const ResultPhraseToneContext = createContext<'neutral' | 'positive'>('neutral');

/** Строки экрана — из кэша старта (HQ-842), свежий ответ ещё не пришёл: у живого матча счёт помечаем «обновляем…».
 *  Контекстом, а не свойством: строки рисуют и общие карточки (MatchListCard) — им знать про кэш не нужно. */
export const RestoredRowsContext = createContext(false);

/** timeOnly — строки уже сгруппированы по дню (заголовок дня над ними): у не начавшегося матча только время.
 *  Без него не сегодняшний старт показывается с датой (HQ-1735).
 *  tone — капсула итога завершённого матча с победителем: neutral (по умолчанию, серая) или win (зелёная, 1025:143087). */
/** anchor — якорь подсветки на строке (тур по приложению HQ-2338: list.matches.first). */
export function MatchRowView({ match, showLeague, onOpen, context, meta = true, timeOnly = false, tone, anchor }: {
  match: MatchRow; showLeague: boolean; onOpen: (id: number) => void; context?: string; meta?: boolean; timeOnly?: boolean;
  tone?: ResultTone; anchor?: string;
}) {
  const formatStart = (iso: string) => formatStartText(iso, { now: new Date(), timeOnly, format: formatDate });
  const model = buildRowModel(match, { showLeague, formatStart, context });
  const open = () => onOpen(match.id);
  const phrase = model.status.kind === 'start' || model.status.kind === 'label' ? null : model.status.text;
  const refreshing = useContext(RestoredRowsContext) && model.isLive;
  const toneFromScreen: ResultTone = useContext(ResultPhraseToneContext) === 'positive' ? 'win' : 'neutral';
  const resultTone = tone ?? toneFromScreen;
  // итог сыгранного: badge «finished» (без плашки или в старом ответе — status «Finished»); отменённый и прочие — серые
  const finished = !model.isLive && (match.badge ? match.badge === 'finished' : match.status === 'Finished');
  const won = finished && model.teams.some((side) => side.isWinner);
  const phraseTone = model.isLive ? styles.phraseLive : won && resultTone === 'win' ? styles.phraseResult : '';
  const startShown = model.status.kind === 'start' && (!match.badge || match.badge === 'pre_match');
  const startDate = startShown ? formatStartParts(match.starting_at, { now: new Date(), timeOnly, format: formatDate }) : null;
  const badge = !startShown && match.badge && match.badge_label && badgeTone(match.badge) !== 'neutral'
    ? { badge: match.badge, label: match.badge_label } : null;
  return (
    <div className={styles.row} role="link" tabIndex={0} onClick={open} onKeyDown={rowKeyHandler(open)} {...anchorAttr(anchor)}>
      {meta && model.meta.length > 0 && <div className={styles.meta}>{model.meta.join(' · ')}</div>}
      <div className={styles.main}>
        {startDate?.date
          // не сегодняшний старт (HQ-1735 v2): дата и время двумя строками — дата переносится, время всегда целиком
          ? <div className={`${styles.side} ${styles.sideDated}`}>
              <span className={styles.startDated}><span className={styles.startDate}>{startDate.date}</span><span className={styles.startTime}>{startDate.time}</span></span>
            </div>
          : startShown
          ? <SideText key={model.status.text} text={model.status.text} />
          : badge
            // цвет — по ключу badge (Figma StatusBadge), не по is_live
            ? <div className={`${styles.side} ${styles.sideWide}`}><StatusBadge badge={badge.badge} label={badge.label} className={styles.sideBadge} /></div>
            : model.isLive
              ? <div className={styles.side}><span className={styles.livePill}><span className={styles.liveDot} />{t('mc_live')}</span></div>
              // неживой итог (ended, Innings break без badge и т. п.) в макете — просто серый текст без плашки
              : <SideText key={match.badge_label ?? match.status_label} text={match.badge === null ? '' : match.badge_label ?? match.status_label} />}
        <div className={styles.teams}>
          {/* ключ — позиция: у финала до жеребьёвки обе команды «TBC» с одним id (живые данные 08.10) */}
          {model.teams.map(({ team, score }, index) => (
            <div key={index} className={styles.team}>
              <TeamLogo src={team.image} code={team.code} size={18} />
              <span className={styles.name}>{team.name}</span>
              {score && <span className={`${styles.score} ${refreshing ? styles.scoreRefreshing : ''}`}>{score}</span>}
            </div>
          ))}
          {refreshing && <div className={styles.refreshing}>{t('tg_updating')}</div>}
        </div>
        <FavoriteStar type="match" id={match.id} />
      </div>
      {phrase && <div className={styles.status}><div className={`caps ${styles.phrase} ${phraseTone}`}>{phrase}</div></div>}
    </div>
  );
}
